import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { GitCompare, X, HelpCircle } from "lucide-react";
import { useResults } from "../data/ResultsContext";
import { freshness } from "../lib/freshness";

/* Hoverable tooltip pill */
function MetricHint({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex ml-1 align-middle">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow((s) => !s)}
        className="w-4 h-4 border border-white/15 hover:border-[#FF4D00]/60 flex items-center justify-center text-neutral-600 hover:text-[#FF4D00] transition-colors cursor-help"
        aria-label="Explain this metric"
      >
        <HelpCircle className="w-2.5 h-2.5" />
      </button>
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-3 py-2 bg-[#0a0a0a] border border-[#FF4D00]/40 text-[10px] font-mono text-neutral-300 leading-relaxed z-50 shadow-[0_0_24px_rgba(255,77,0,0.15)] whitespace-normal">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] border-b border-r border-[#FF4D00]/40 rotate-45 -mt-1" />
        </span>
      )}
    </span>
  );
}

const GRID_IMG =
  "https://static.prod-images.emergentagent.com/jobs/174e7555-4c1b-4694-85af-3ff24499f4d9/images/a0e8c7e8820996b580cc0277ed08dc40d00f2c55297cf2269a90b8b395063512.png";

const METRIC_FIELDS = [
  { key: "eci", label: "ECI", hint: "Composite calibration index", explain: "Epistemic Calibration Index — how well the model knows what it knows, combining accuracy with confidence calibration." },
  { key: "sda", label: "SDA", hint: "Solvency-class Discrimination", explain: "Solvency-class Discrimination Accuracy — can the model tell solvable from unsolvable questions?" },
  { key: "ca", label: "CA", hint: "Calibrated Abstention", explain: "Calibrated Abstention — does the model refuse to answer when it should? Higher = better restraint." },
  {
    key: "metacog_readiness_score",
    label: "Metacog",
    hint: "Readiness score (≥0.75 = frontier)",
    explain: "Metacognitive Readiness Score — composite measure of self-awareness. ≥0.75 qualifies as 'frontier-tier'.",
  },
  {
    key: "overconfidence_gap",
    label: "Overconf Gap",
    hint: "Claimed vs realised (lower = better)",
    lowerBetter: true,
    explain: "How much the model overestimates its own accuracy. 0 = perfectly calibrated. The gap is claimed accuracy minus actual accuracy.",
  },
  {
    key: "agi_progress_pct",
    label: "AGI %",
    hint: "Progress toward 0.85 AGI target",
    suffix: "%",
    explain: "Percentage progress toward the 0.85 ECI threshold that PROMETHEUS defines as AGI-grade metacognition.",
  },
];

function fmt(v, digits = 3, suffix = "") {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits) + suffix;
}

function DeltaPill({ a, b, lowerBetter }) {
  if (a == null || b == null) return null;
  const d = a - b;
  const better = lowerBetter ? d < 0 : d > 0;
  const color = Math.abs(d) < 1e-4 ? "#737373" : better ? "#FF4D00" : "#00F0FF";
  const sign = d > 0 ? "+" : "";
  return (
    <span
      className="font-mono text-[10px] tracking-[0.18em] uppercase ml-2 px-1.5 py-0.5 border"
      style={{ color, borderColor: color + "55" }}
      data-testid="delta-pill"
    >
      Δ {sign}
      {d.toFixed(3)}
    </span>
  );
}

function FreshnessBadge({ iso, testid = "freshness-badge" }) {
  const f = freshness(iso);
  return (
    <div
      data-testid={testid}
      className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border"
      style={{ borderColor: f.color + "66", color: f.color }}
      title={iso}
    >
      <span
        className={"w-1.5 h-1.5 rounded-full " + (f.pulse ? "pulse-dot" : "")}
        style={{ background: f.color }}
      />
      {f.label}
    </div>
  );
}

function MetricTile({ f, a, b, compare }) {
  const digits = f.key === "agi_progress_pct" ? 1 : 3;
  const suffix = f.suffix || "";
  return (
    <motion.div
      data-testid={`metric-${f.key}`}
      whileHover={{ y: -2 }}
      className="border border-white/10 p-5 bg-[#0d0d0d] hover:border-[#FF4D00]/50 transition-colors relative group z-10 hover:z-50"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#FF4D00]">
          {f.label}
        </div>
        {compare && <DeltaPill a={a[f.key]} b={b[f.key]} lowerBetter={f.lowerBetter} />}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={a.id + (compare ? "-vs-" + b.id : "")}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex items-baseline gap-2 flex-wrap"
        >
          <span className="font-serif-display text-4xl lg:text-5xl text-[#FF4D00] glow-fire">
            {fmt(a[f.key], digits, suffix)}
          </span>
          {compare && (
            <>
              <span className="text-neutral-700 font-mono text-sm">vs</span>
              <span className="font-serif-display text-3xl text-[#00F0FF] glow-cyan">
                {fmt(b[f.key], digits, suffix)}
              </span>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-2 flex items-center gap-1 text-[10px] font-mono text-neutral-600 uppercase tracking-widest">
        {f.hint}
        {f.explain && <MetricHint text={f.explain} />}
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF4D00]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.div>
  );
}

export default function ModelExplorer() {
  const { results } = useResults();
  const [primaryId, setPrimaryId] = useState(results.models[0]?.id);
  const [compareId, setCompareId] = useState(null); // null = single-model mode

  const primary = useMemo(
    () => results.models.find((m) => m.id === primaryId) || results.models[0],
    [primaryId, results.models],
  );
  const compareModel = useMemo(
    () =>
      compareId ? results.models.find((m) => m.id === compareId) : null,
    [compareId, results.models],
  );

  const isCompare = !!compareModel;

  const radarData = [
    { axis: "SDA", primary: primary.sda, compare: compareModel?.sda },
    { axis: "CA", primary: primary.ca, compare: compareModel?.ca },
    { axis: "RP", primary: primary.rp, compare: compareModel?.rp },
    {
      axis: "1−ECE",
      primary: primary.one_minus_ece,
      compare: compareModel?.one_minus_ece,
    },
    {
      axis: "1−HGI",
      primary: primary.one_minus_hgi,
      compare: compareModel?.one_minus_hgi,
    },
  ];

  const handleToggle = (id) => {
    // If the user clicks the currently-primary pill → no-op
    if (id === primaryId) return;
    if (isCompare && id === compareId) {
      // click the active compare pill again → drop it
      setCompareId(null);
      return;
    }
    if (isCompare) {
      // already in compare mode → switch compare target
      setCompareId(id);
      return;
    }
    // not in compare mode → switch primary
    setPrimaryId(id);
  };

  const handleCompareButton = () => {
    if (isCompare) {
      setCompareId(null);
      return;
    }
    // enter compare mode: pick first model that isn't primary
    const candidate = results.models.find((m) => m.id !== primaryId);
    setCompareId(candidate?.id || null);
  };

  return (
    <section
      id="explorer"
      data-testid="explorer-section"
      className="relative py-32 px-6 lg:px-10 border-t border-white/5 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${GRID_IMG})` }}
      />
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 tech-grid opacity-30" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex items-end justify-between flex-wrap gap-6"
        >
          <div>
            <div className="chapter-label mb-6">
              03 · Interactive Model Explorer
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-neutral-100 max-w-4xl">
              Pick a model.
              <span className="text-[#FF4D00]">
                {" "}
                Watch its metacognition bleed.
              </span>
            </h2>
          </div>
          <button
            onClick={handleCompareButton}
            data-testid="toggle-compare-btn"
            className={
              "font-mono text-[11px] tracking-[0.22em] uppercase px-4 py-2.5 border transition-all duration-300 flex items-center gap-2 " +
              (isCompare
                ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10 shadow-[0_0_18px_rgba(0,240,255,0.25)]"
                : "border-white/15 text-neutral-300 hover:border-[#00F0FF]/60 hover:text-[#00F0FF]")
            }
          >
            {isCompare ? (
              <>
                <X className="w-3.5 h-3.5" /> exit compare
              </>
            ) : (
              <>
                <GitCompare className="w-3.5 h-3.5" /> compare mode
              </>
            )}
          </button>
        </motion.div>

        {/* Hint bar in compare mode */}
        <AnimatePresence>
          {isCompare && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mb-6 border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-4 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[#00F0FF]"
            >
              <span className="inline-block w-2 h-2 bg-[#FF4D00] mr-3 align-middle" />
              {primary.short}
              <span className="text-neutral-500 mx-2">·vs·</span>
              <span className="inline-block w-2 h-2 bg-[#00F0FF] mr-3 align-middle" />
              {compareModel.short}
              <span className="text-neutral-500 ml-4 tracking-normal lowercase italic">
                click any model to swap the cyan profile · click again to drop
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Model toggles */}
        <div
          className="flex flex-wrap justify-center gap-2 mb-10"
          data-testid="model-toggle-group"
        >
          {results.models.map((m) => {
            const isPrimary = m.id === primaryId;
            const isCompareTarget = m.id === compareId;
            const isActive = isPrimary || isCompareTarget;
            const f = freshness(m.generated_at);
            const borderColor = isPrimary
              ? "#FF4D00"
              : isCompareTarget
                ? "#00F0FF"
                : undefined;
            return (
              <button
                key={m.id}
                data-testid={`model-toggle-${m.id}`}
                onClick={() => handleToggle(m.id)}
                className={
                  "font-mono text-[11px] tracking-[0.25em] uppercase px-4 py-2.5 border transition-all duration-300 flex items-center gap-2 " +
                  (isActive
                    ? "bg-white/5"
                    : "border-white/10 text-neutral-500 hover:border-white/30 hover:text-neutral-200")
                }
                style={
                  isActive
                    ? {
                        borderColor,
                        color: borderColor,
                        boxShadow: `0 0 18px ${borderColor}40`,
                      }
                    : undefined
                }
              >
                <span
                  className={
                    "w-1.5 h-1.5 rounded-full " + (f.pulse ? "pulse-dot" : "")
                  }
                  style={{ background: f.color }}
                />
                {m.short}
                <span className="text-[9px] opacity-60">
                  {m.benchmark_mode === "DEEP_PROBE" ? "· DEEP" : "· EXT"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left: metric tiles */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <FreshnessBadge iso={primary.generated_at} />
              <div
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-white/10 text-neutral-400"
                data-testid="mode-badge"
              >
                {primary.benchmark_mode} · n={primary.n}
              </div>
              <div
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-white/10 text-neutral-400"
                title={primary.metacog_tier}
                data-testid="tier-badge"
              >
                {primary.metacog_tier.replace(/_/g, " ")}
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              {METRIC_FIELDS.map((f) => (
                <MetricTile
                  key={f.key}
                  f={f}
                  a={primary}
                  b={compareModel || primary}
                  compare={isCompare}
                />
              ))}
            </div>

            {/* Meta (primary only) */}
            <motion.div
              key={primary.id + "-meta"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-6 border border-white/10 bg-[#0d0d0d] p-6"
            >
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="font-mono text-[10px] text-neutral-600 tracking-widest">
                    {primary.vendor.toUpperCase()} · {primary.model_id}
                  </div>
                  <div className="font-serif-display text-3xl text-neutral-100 mt-1">
                    {primary.name}
                  </div>
                </div>
                <div className="font-mono text-[11px] text-[#00F0FF] tracking-[0.3em]">
                  D-PRIME · {primary.d_prime.toFixed(4)}
                </div>
              </div>
              <p className="text-neutral-400 text-sm leading-relaxed italic">
                {primary.note}
              </p>
            </motion.div>
          </div>

          {/* Right: radar (with overlay in compare mode) */}
          <div className="lg:col-span-5 flex flex-col h-full">
            <div className="border border-white/10 bg-[#0d0d0d] p-6 h-full flex flex-col">
              <div className="flex items-baseline justify-between mb-1">
                <div className="font-mono text-[11px] tracking-[0.3em] text-[#00F0FF]">
                  EPISTEMIC · PROFILE · RADAR
                </div>
                {isCompare && (
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500">
                    overlay · mode
                  </div>
                )}
              </div>
              <div className="font-serif-display italic text-xl text-neutral-200 mb-4">
                5 axes · SDA · CA · RP · 1−ECE · 1−HGI
              </div>
              <div className="flex-1 min-h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="70%">
                    <PolarGrid stroke="#2a2a2a" />
                    <PolarAngleAxis
                      dataKey="axis"
                      tick={{
                        fill: "#a3a3a3",
                        fontSize: 11,
                        fontFamily: "JetBrains Mono",
                      }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 1]}
                      tick={{
                        fill: "#555",
                        fontSize: 9,
                        fontFamily: "JetBrains Mono",
                      }}
                      stroke="#2a2a2a"
                    />
                    <Radar
                      name={primary.short}
                      dataKey="primary"
                      stroke="#FF4D00"
                      fill="#FF4D00"
                      fillOpacity={0.38}
                      animationDuration={900}
                    />
                    {isCompare && (
                      <Radar
                        name={compareModel.short}
                        dataKey="compare"
                        stroke="#00F0FF"
                        fill="#00F0FF"
                        fillOpacity={0.22}
                        animationDuration={900}
                      />
                    )}
                    <Tooltip formatter={(v) => Number(v).toFixed(4)} />
                    {isCompare && (
                      <Legend
                        wrapperStyle={{
                          fontFamily: "JetBrains Mono",
                          fontSize: 11,
                          color: "#a3a3a3",
                          paddingTop: 10,
                        }}
                      />
                    )}
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
